import type { App } from './App';
import { modal } from '../ui/components';
import { h } from '../ui/dom';
import { t } from '../i18n';

const IOS_HINT_KEY = 'paper-legends/home-screen-hint';

type FsDoc = Document & { webkitFullscreenEnabled?: boolean; webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
type FsEl = HTMLElement & { webkitRequestFullscreen?: () => void };

/** A phone or small tablet held in the hand. */
export function isPhone(): boolean {
  return matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 820;
}

/** Opened from the home screen (installed): already full screen. */
export function isInstalled(): boolean {
  return matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

export function canFullscreen(): boolean {
  const d = document as FsDoc;
  return Boolean(d.fullscreenEnabled || d.webkitFullscreenEnabled);
}

export function isFullscreen(): boolean {
  const d = document as FsDoc;
  return Boolean(d.fullscreenElement || d.webkitFullscreenElement);
}

/** Must run inside a tap or click. */
export async function enterFullscreen(): Promise<void> {
  const el = document.documentElement as FsEl;
  try {
    if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
    else el.webkitRequestFullscreen?.();
    // Keep the layout the player chose; just stop accidental rotation where allowed.
    await (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.(screen.orientation.type).catch(() => {});
  } catch {
    /* refused (e.g. not from a tap): stay as is */
  }
}

/**
 * Phones: full screen and turned sideways for the big drawing page. Android can lock it;
 * where locking is not allowed (iPhone Safari) it returns false. Must run inside a tap.
 */
export async function enterLandscape(): Promise<boolean> {
  if (canFullscreen() && !isFullscreen()) await enterFullscreen();
  try {
    const o = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
    if (!o?.lock) return false;
    await o.lock('landscape'); // resolves once the lock is granted (the turn itself follows)
    return true;
  } catch {
    return false;
  }
}

/** Lets the phone turn freely again (full screen stays). */
export function releaseOrientation(): void {
  try {
    screen.orientation?.unlock?.();
  } catch {
    /* not locked */
  }
}

export function exitFullscreen(): void {
  const d = document as FsDoc;
  if (d.exitFullscreen) void d.exitFullscreen().catch(() => {});
  else d.webkitExitFullscreen?.();
}

export function toggleFullscreen(): void {
  if (isFullscreen()) exitFullscreen();
  else void enterFullscreen();
}

/**
 * Phones play full screen: the first tap enters it (browsers only allow it
 * from a tap), and coming back to the game arms it again. iPhones cannot do
 * this in Safari, so they get a one-time "Add to Home Screen" tip instead.
 */
export function setupPhoneFullscreen(app: App): void {
  if (!isPhone() || isInstalled()) return;
  if (!canFullscreen()) {
    void homeScreenTip();
    return;
  }
  let armed = true;
  const onTap = (): void => {
    if (!armed || !app.store.state.settings.fullscreenOnPhone || isFullscreen()) return;
    armed = false;
    void enterFullscreen();
  };
  document.addEventListener('pointerup', onTap, true);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') armed = true;
  });
}

/** iPhone Safari: full screen only works after "Add to Home Screen". Shown once. */
async function homeScreenTip(): Promise<void> {
  try {
    if (localStorage.getItem(IOS_HINT_KEY)) return;
    localStorage.setItem(IOS_HINT_KEY, '1');
  } catch {
    return;
  }
  await new Promise((r) => setTimeout(r, 4000)); // after the first screens settle
  if (document.querySelector('.modal-wrap')) return;
  await modal(
    t('Play full screen'),
    h(
      'div',
      { class: 'home-screen-tip' },
      h('p', null, t('For full screen on iPhone, add the game to your home screen:')),
      h('ol', null, h('li', null, t('Tap the Share button (the square with an arrow).')), h('li', null, t('Choose "Add to Home Screen".')), h('li', null, t('Open Paper Legends from your home screen.'))),
    ),
    [{ label: t('Got it'), value: 'ok', variant: 'primary', color: 'yellow' }],
  );
}
